import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router';

import useRequest from '@swf/core/hooks/useRequest';
import type { Dispatch } from '@swf/core/store';
import { UserClubRole } from '@swf/core/types/Poker';
import type {
  ChangePlayersStatusRequest,
  ChangePlayersStatusResponse,
  CloseClubRequest,
  CloseClubResponse
} from '@swf/generated/types/api/v0/SwHomeGames';
import { handleServerResponse, useGetContentRoutes, useHomeGamesWidget } from '@swf/poker';

const parseResponse = (
  body: CloseClubResponse | ChangePlayersStatusResponse,
  isManager: boolean
) => {
  if (isManager) {
    return handleServerResponse<CloseClubResponse, CloseClubRequest>({
      response: body,
      accessor: 'closeClubReply'
    });
  }

  return handleServerResponse<ChangePlayersStatusResponse, ChangePlayersStatusRequest>({
    response: body,
    accessor: 'changePlayersStatusReply'
  });
};

const useCloseOrLeaveClub = () => {
  const { widgetDispatch } = useHomeGamesWidget();
  const request = useRequest();
  const [loading, setLoading] = useState(false);
  const { clubsPath } = useGetContentRoutes();
  const history = useHistory();
  const { search } = useLocation();
  const { modal } = useDispatch<Dispatch>();

  const {
    params: { clubId }
  } = useRouteMatch<{ clubId: string }>();
  const action = (userRole: UserClubRole) => {
    setLoading(true);
    const isManager = userRole === UserClubRole.Manager;

    request({
      service: `homeGames.${isManager ? 'closeClubRequest' : 'leaveClubRequest'}`,
      body: { clubId: Number(clubId) }
    }).subscribe({
      next: ({ body }) => {
        const { error } = parseResponse(body, isManager);

        if (error) {
          modal.enqueue({
            path: '/notifications/simple-notification',
            locationState: { msg: error }
          });
        } else {
          widgetDispatch.getUserClubs();
          history.replace({ pathname: clubsPath, search });
        }

        setLoading(false);
      }
    });
  };

  return { action, loading };
};

export default useCloseOrLeaveClub;
